// kit — THE DESIGN SYSTEM'S PIECES (docs/DESIGN-SYSTEM.md §8).
//
// A screen is built from these and the Ledger primitive, so that "what a label looks like", "what a
// state line looks like", "how an address is shown" is decided HERE, once — not re-drawn per screen
// (21 Oct 2026: 583 hand-set sizes in 17 sizes across 64 files, every fix local to one screen).
//
//   Heading     a card's or section's title                     text-title
//   Label       a column head or strip label — a word or three   text-label, uppercase
//   Value       what he reads: a name, a figure, a stamp          text-body (mono for figures)
//   Money       a figure and its muted unit, signed or not        Amount / Delta of the Ledger
//   Address     a wallet address, shortened, whole on tap         mono, Copy
//   StateLine   ONE line: what happened + what to do; detail behind a tap
//   EmptyState  one muted line and at most one action
//   Card        the panel: a title row, then the content
//
// plus the older pieces that stand beside them and are re-exported so a screen imports from one place:
// ErrorLine / WarnLine, Pill, Pending, MoreDetails.
//
// THE WORD BUDGET (scripts/check-word-budget.mjs counts it): a Label is ≤ 3 words, a StateLine ≤ 10
// words of its own, an EmptyState ≤ 6; explanation goes in `more` (a tap) or the screen's help=.
// THE TYPE SCALE (scripts/check-type-scale.mjs): these pieces name a role, never a number — and so
// does every screen that uses them.
import { useState, type ReactNode } from 'react';
import { Amount, Delta } from './Ledger';
import { CopyRef } from './CopyRef';
import { ApiError, errorWords, isCustodySignin, readWords } from '../lib/errors';
import { SigninQuiet } from './v2/custody/SigninLine';
import { railName } from '../lib/naming';
import { offlineFailure } from '../lib/read-state';
import { uiV2 } from '../lib/ui-v2';
import type { RailValue } from './RailPicker';

export { ErrorLine, WarnLine } from './ErrorLine';
export { Pill } from './Pill';
export { Pending } from './Pending';
export { MoreDetails } from './MoreDetails';

/** A card's or section's title; `action` stands at the right (a link, a count, a button). */
export function Heading({ children, action, as: Tag = 'h2', className = '' }: {
  children: ReactNode;
  action?: ReactNode;
  as?: 'h1' | 'h2' | 'h3';
  className?: string;
}) {
  return (
    <div className={`flex items-baseline justify-between gap-3 ${className}`}>
      <Tag className="min-w-0 text-title font-semibold">{children}</Tag>
      {action && <div className="shrink-0 text-body">{action}</div>}
    </div>
  );
}

/** A column head or a strip label: a word, or three — never a sentence. Always uppercase, always muted. */
export function Label({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <span className={`text-label font-medium uppercase tracking-wider text-tg-hint whitespace-nowrap ${className}`}>{children}</span>;
}

/** What he reads: a name, a stamp, a figure. `mono` is for figures and ids; `strong` for the one that answers the question. */
export function Value({ children, mono = false, strong = false, className = '' }: {
  children: ReactNode;
  mono?: boolean;
  strong?: boolean;
  className?: string;
}) {
  return <span className={`text-body ${mono ? 'font-mono tabular' : ''} ${strong ? 'font-medium' : ''} ${className}`}>{children}</span>;
}

/** A figure and its muted unit: `1,008,212.50 AED`. `signed` draws a movement with its sign (+ / −, the negative in
 *  the negative colour, a dash for nothing); `size="figure"` is for the one to four key figures of a screen. The
 *  number is the Ledger's own rendering (fmtAmount: international grouping, fiat whole or two places). */
export function Money({ value, asset, signed = false, size = 'body' }: {
  value: string | number;
  asset: string;
  signed?: boolean;
  size?: 'body' | 'figure';
}) {
  const n = typeof value === 'number' ? value : Number(value);
  const inner = signed ? <Delta value={n} asset={asset} code /> : <Amount value={value} asset={asset} />;
  return size === 'figure' ? <span className="text-figure font-semibold">{inner}</span> : inner;
}

/** A wallet address as the desk shows it: first eight, last four. The whole address is what Copy takes. */
export const shortAddress = (a: string): string => (a.length <= 16 ? a : `${a.slice(0, 8)}…${a.slice(-4)}`);

/** An address: mono, shortened, whole on tap/hover with Copy. A look-alike (an address-poisoning attempt) is red,
 *  and the screen says why ONCE, in a StateLine — never on every row. `copy={false}`: poisoning the chain sync parked
 *  (lib/chain-poison.ts, 4 Nov 2026) — the address is shown, never offered to copy. */
export function Address({ value, lookalike = false, copy = true, className = '' }: { value: string; lookalike?: boolean; copy?: boolean; className?: string }) {
  return (
    <span className={`inline-flex min-w-0 items-center ${className}`} data-address={lookalike ? 'lookalike' : 'plain'}>
      {copy
        ? <CopyRef value={value} label={shortAddress(value)} tone={lookalike ? 'neg' : 'muted'} />
        : <span className={`font-mono text-meta ${lookalike ? 'text-neg' : 'text-tg-hint'}`} title="Address poisoning — never pay, never copy" data-no-copy="">{shortAddress(value)}</span>}
    </span>
  );
}

const STATE_TONE = { neutral: 'text-tg-hint', good: 'text-pos', warn: 'text-warn', bad: 'text-neg' } as const;
export type StateTone = keyof typeof STATE_TONE;

/** ONE LINE: what happened and what to do ("Couldn't read the chain — try again."). The tone is the severity (red
 *  refuses or fails, amber warns, grey is plain, green is done); the words carry the meaning. `more` is the detail
 *  — a server's whole sentence, the figures behind it — and opens on a tap; `action` is the one button or link. */
export function StateLine({ tone = 'neutral', children, more, action, className = '' }: {
  tone?: StateTone;
  children: ReactNode;
  more?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={className}>
      <p role={tone === 'bad' ? 'alert' : undefined} className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 text-body ${STATE_TONE[tone]}`}>
        <span className="min-w-0">{children}</span>
        {more && (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            // A phone's target is 44 px (§4): the 16 px text line grows by 14 px above and below (44) and 6 px at the sides — padding that an
            // equal negative margin takes back, so the line is no taller and no wider.
            className="text-meta text-tg-hint underline-offset-2 hover:text-tg-text hover:underline max-lg:-mx-1.5 max-lg:-my-3.5 max-lg:px-1.5 max-lg:py-3.5"
          >
            {open ? 'Less' : 'Details'}
          </button>
        )}
        {action}
      </p>
      {more && open && <div className="mt-1.5 space-y-1 border-l border-stroke pl-3 text-meta text-tg-hint">{more}</div>}
    </div>
  );
}

/** Nothing here: ≤ 6 words, muted, and at most one action. Never a card of its own for two words. */
export function EmptyState({ children, action, className = '' }: { children: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={`py-6 text-center ${className}`}>
      <p className="text-body text-tg-hint">{children}</p>
      {action && <div className="mt-2 text-body">{action}</div>}
    </div>
  );
}

/** The class of a small link or button that is the way out of a line (Try again, Book it from Chain →, +2 more). A phone's
 *  target is 44px (design system §4); the text stays 12px on its 16px line and the hit area grows by 14px above and below
 *  (16 + 2×14 = 44) — padding that an equal negative margin takes back, so the line is no taller. The area overlaps the
 *  text lines around it (not other controls on the same line: those sit a gap apart). An inline link ignores the margin and
 *  keeps the padding, which is what is wanted. */
export const TAP = 'btn-link text-meta max-lg:py-3.5 max-lg:-my-3.5';

/** A server's sentence split at its first full stop that a space follows: the first sentence is the line, the rest folds
 *  behind Details. "3.672" and "36,720.50 AED" hold no such stop, so a figure never splits a sentence. */
export function sayFirst(text: string): { head: string; rest: string } {
  const t = text.trim();
  const m = /^([\s\S]+?[.!?])\s+(?=\S)/.exec(t);
  return m ? { head: m[1] ?? t, rest: t.slice(m[0].length).trim() } : { head: t, rest: '' };
}

/** A read that failed: ONE line — what failed, and the way out — with the server's own words behind Details. Never drawn
 *  like an empty read: a list that could not be read is not a list with nothing in it. Amber by default (the screen may
 *  still show what it last read); red where there is nothing else to show. */
export function ReadFailed({ what, error, onRetry, tone = 'warn', className = '' }: {
  what: string; error: unknown; onRetry?: () => void; tone?: 'bad' | 'warn'; className?: string;
}) {
  // THE REBUILT CONSOLE (uiV2, switched off today) says a read that failed only for want of a connection is WAITING, not a
  // failure (lib/read-state.ts offlineFailure; the energy lane's finding, 27 Oct 2026); its shell reads again when the
  // connection is back, so there is no Try again to press. Today's console is untouched.
  if (uiV2(1) && offlineFailure(error)) return <OfflineLine what={what} className={className} />;
  // The lapsed custody sign-in is said once, by the custody shell's line — never a red line per read (2 Nov 2026).
  if (isCustodySignin(error)) return <SigninQuiet error={error} className={className} />;
  return (
    <StateLine
      tone={tone}
      className={className}
      more={<span className="break-words">{readWords(error)}</span>}
      action={onRetry && <button type="button" className={TAP} onClick={onRetry}>Try again</button>}
    >
      Couldn&apos;t read {what}.
    </StateLine>
  );
}

/** A read waiting for the connection (the rebuilt console): grey, one line, what it waits to read, and that it reads again
 *  by itself — no Try again, which would only fail again offline. */
export function OfflineLine({ what, kept = false, className = '' }: { what: string; kept?: boolean; className?: string }) {
  return (
    <StateLine tone="neutral" className={className} more="This device is offline. It is read again as soon as the connection is back.">
      {kept ? <>Offline — showing the last read.</> : <>Offline — waiting to read {what}.</>}
    </StateLine>
  );
}

/** The books' answer to a button press that was refused or failed. A refusal (a 4xx — the books answered before writing)
 *  shows its FIRST sentence and folds the rest — unless the whole of it is short (twelve words: the budget's own line
 *  between a sentence and a paragraph), when the "what to do" half is worth more on the line than a tap. A 5xx or a lost
 *  connection MAY HAVE PARTLY POSTED, and its sentence is the guard ("check the trades log before posting this again"),
 *  so that one shows whole. */
export function ServerLine({ error, tone = 'bad', className = '' }: { error: unknown; tone?: 'bad' | 'warn'; className?: string }) {
  const whole = errorWords(error);
  const mayHavePosted = !(error instanceof ApiError) || error.status >= 500;
  const short = whole.split(/\s+/).filter(Boolean).length <= 12;
  const { head, rest } = mayHavePosted || short ? { head: whole, rest: '' } : sayFirst(whole);
  // A server sentence can carry a transaction hash or an account path with no place to break: it breaks inside the word.
  return (
    <StateLine tone={tone} className={className} more={rest ? <span className="break-words">{rest}</span> : undefined}>
      <span className="break-words">{head}</span>
    </StateLine>
  );
}

/** WHERE THE ACCOUNT PICKER WOULD BE when the account list could not be read. An open picker with no rows says "No such
 *  account. New accounts are opened from Parties → New…" — a missing-record claim, and a way to make a duplicate, under a
 *  banner that says the READ failed. This stands in for it: the chosen account (if any) by name, no list to open, no
 *  claim. The banner above carries the failure and the Retry. (RailPicker's own readFailed does the same for a picker
 *  that stays on screen over a cached list.) */
export function AccountsDown({ value }: { value: RailValue }) {
  return (
    <div className="input w-full flex justify-between items-center gap-2 text-tg-hint" aria-disabled="true">
      <span className="min-w-0 flex-1 truncate">{value.slug ? railName({ slug: value.slug }) : 'Pick an account'}</span>
      <span className="shrink-0 text-meta">Accounts not read</span>
    </div>
  );
}

/** The panel: a title row (title left, `action` right), then the content. `tone` rims it when the card IS the
 *  alert (red: something failed; amber: something needs him) — a card is calm unless it has something to say. */
export function Card({ title, action, tone, children, className = '' }: {
  title?: ReactNode;
  action?: ReactNode;
  tone?: 'warn' | 'bad';
  children: ReactNode;
  className?: string;
}) {
  const rim = tone === 'bad' ? 'border-neg/40' : tone === 'warn' ? 'border-warn/40' : '';
  return (
    <section className={`card ${rim} ${className}`}>
      {(title || action) && <Heading as="h2" action={action} className="mb-2">{title}</Heading>}
      {children}
    </section>
  );
}
